import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { AssignedContact, ClientTaskGroup, ClientTaskGroupType, TaskStatus, WidgetData } from './client-task.models';

@Injectable({
	providedIn: 'root'
})
export class ClientTaskWidgetService {
	esignLabel: string;
	approvalLabel: string;
	docReviewLabel: string;
	documentRequestLabel: string;
	invoiceLabel: string;
	paymentRequestLabel: string;
	questionnaireLabel: string;
	pastDueLabel: string;
	waitingOnClientLabel: string;
	completedLabel: string;
	canceledLabel: string;
	receivedLabel: string;
	client: cs.IClientWithExpansionV3Dto;
	currentLang: string;
	contactInfoCache: Map<string, [string, boolean]> = new Map();

	constructor(private http: HttpClient,  private translate: TranslateService) { }

	async getWidgetTaskGroups(tasks: ClientTaskGroup[], client: cs.IClientWithExpansionV3Dto): Promise<WidgetData[]> {
		this.currentLang = this.translate.currentLang;
		this.client = client;
		await this.setLabels();
		const widgetGroups: WidgetData[] = [];
		for (const task of tasks) {
			const data = await this.setData(task);
			widgetGroups.push(data);
		}
		return widgetGroups.sort((a, b) => {
			if (a.label.toLowerCase() < b.label.toLowerCase()) {
				return -1;
			}
			if (a.label.toLowerCase() > b.label.toLowerCase()) {
				return 1;
			}
			return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
		});
	}

	async getClientCenterTasks(clientId: string): Promise<ClientTaskGroup[]> {
		try {
			return await this.http.get<ClientTaskGroup[]>(`/api/client-center/v3/tasks/groups/clients/${clientId}`)
				.pipe(
					// filter bad data and task types that are not supported
					map(groups => groups.filter(group => group.tasks[0] && Object.values(ClientTaskGroupType).includes(group.type))),
					map(groups => groups.map(group => {
						if (!group.dueDate) {
							group.dueDate = group.tasks[0].dueDate;
						}
						if (group.status === TaskStatus.NotStarted) {
							// Show anything with status new as waiting on client
							group.status = TaskStatus.WOC;
						}
						return group;
					})),
				).toPromise();
		} catch {
			return [];
		}
	}

	async setLabels() {
		this.esignLabel = await this.translate.get('onvio.clientTaskWidget.taskGroupLabel.esignRequest').toPromise();
		this.approvalLabel = await this.translate.get('onvio.clientTaskWidget.taskGroupLabel.approvalRequest').toPromise();
		this.docReviewLabel = await this.translate.get('onvio.clientTaskWidget.taskGroupLabel.documentReview').toPromise();
		this.documentRequestLabel = await this.translate.get('onvio.clientTaskWidget.taskGroupLabel.documentRequest').toPromise();
		this.invoiceLabel = await this.translate.get('onvio.clientTaskWidget.taskGroupLabel.invoice').toPromise();
		this.paymentRequestLabel = await this.translate.get('onvio.clientTaskWidget.taskGroupLabel.paymentRequest').toPromise();
		this.questionnaireLabel = await this.translate.get('onvio.clientTaskWidget.taskGroupLabel.taxQuestionnaire').toPromise();
		this.pastDueLabel = await this.translate.get('Bluemoon.Practice.Widget.PriorInvoices.Overdue').toPromise();
		this.waitingOnClientLabel = await this.translate.get('onvio.clientTaskWidget.status.waitingOnClient').toPromise();
		this.completedLabel = await this.translate.get('onvio.clientTaskWidget.status.completed').toPromise();
		this.canceledLabel = await this.translate.get('onvio.clientTaskWidget.status.canceled').toPromise();
		this.receivedLabel = await this.translate.get('onvio.clientTaskWidget.status.received').toPromise();
		this.pastDueLabel = await this.translate.get('Bluemoon.Practice.Widget.PriorInvoices.Overdue').toPromise();
	}

	async setData(taskGroup: ClientTaskGroup) {
		const data = {
			id: taskGroup.id,
			dueDate: new Date(taskGroup.dueDate.split('-').join('/')).toLocaleDateString(this.currentLang),
			assignedContacts: await this.getAssignedContactsList(taskGroup),
			label: this.getLabel(taskGroup),
			statusLabel: this.getStatusLabel(taskGroup),
			status: taskGroup.status,
			pastDue: this.isPastDue(taskGroup),
			subject: this.getSubject(taskGroup),
			canEdit: taskGroup.type === ClientTaskGroupType.Document_Request,
			type: taskGroup.type,
			showBusyLoader: false,
			showTooltip: false,
			tooltipChecked: false
		};
		return data;
	}

	async getDocRequestSubject(taskGroup: ClientTaskGroup): Promise<string> {
		const id = taskGroup.tasks[0].params?.documentRequestId;
		const url = `/api/storage/v2/requests/${id}`;
		const resp = await this.http.get<any>(url).toPromise();
		return resp.data.subRequests.map(s => s.details).join(', ');
	}

	async getDocReviewSubject(taskGroup: ClientTaskGroup): Promise<string> {
		if (taskGroup.status === TaskStatus.WOC) {
			const url = taskGroup.tasks[0].params?.viewUrl.replace(/^https?:\/\/(\w+\.?)+/, '');
			const resp = await this.http.get<any>(url).toPromise();
			return resp.data.subRequests[0].documents.map(s => s.documentName).join(', ');
		}
	}

	isPastDue(taskGroup: ClientTaskGroup) {
		return Date.now() > new Date(taskGroup.dueDate).getTime() && taskGroup.status === TaskStatus.WOC;
	}

	async getAssignedContactsList(taskGroup: ClientTaskGroup): Promise<AssignedContact []> {
		if (taskGroup.tasks[0].assignedContacts?.length > 0) {
			const assignedContacts: AssignedContact[] = [];
			taskGroup.tasks[0].assignedContacts.forEach(async contact => {
				const [userId, isClientCenterEnabled] = await this.getContactInfo(this.client.companyId, contact.id);
				contact.userId = userId;
				contact.clientCenterEnabled = isClientCenterEnabled;
				contact.showTooltip = false;
				contact.tooltipChecked = false;
				assignedContacts.push(contact);
			});

			return assignedContacts;
		} else {
			const [userId, isClientCenterEnabled] = await this.getContactInfo(this.client.companyId, this.client.primaryContact.id);
			return [{
				name: this.client.primaryContactExpanded?.displayAs,
				id: this.client.primaryContact.id,
				userId,
				tooltipChecked: false,
				showTooltip: false,
				clientCenterEnabled: isClientCenterEnabled
			}];
		}
	}

	getLabel(taskGroup: ClientTaskGroup): string {
		switch (taskGroup.type) {
		case ClientTaskGroupType.Esign:
			const isApproval = taskGroup.tasks[0]?.label === 'Approval Request';
			return isApproval ? this.approvalLabel : this.esignLabel;
		case ClientTaskGroupType.Questionnaire:
			return this.questionnaireLabel;
		case ClientTaskGroupType.Document_Request:
			return this.documentRequestLabel;
		case ClientTaskGroupType.Document_Review:
			return this.docReviewLabel;
		case ClientTaskGroupType.Payment_Request:
			return this.paymentRequestLabel;
		}
	}

	getStatusLabel(taskGroup: ClientTaskGroup): string {
		switch (taskGroup.status) {
		case TaskStatus.WOC:
			return this.waitingOnClientLabel;
		case TaskStatus.Completed:
			return this.completedLabel;
		case TaskStatus.Canceled:
			return this.canceledLabel;
		case TaskStatus.Received:
			return this.receivedLabel;
		}
	}

	getSubject(taskGroup: ClientTaskGroup) {
		switch (taskGroup.type) {
		case ClientTaskGroupType.Payment_Request:
			return this.invoiceLabel + ' #' + taskGroup.tasks[0].params?.invoiceNumber;
		case ClientTaskGroupType.Questionnaire:
			const year = taskGroup.tags?.find(t => /\b(20)\d{2}\b/.exec(t));
			const isUK = taskGroup.tags?.find(t => t === 'UKPersonalTaxInfoRequest');
			if (isUK) {
				const yearString: string = '' + Number(year);
				return (Number(year) - 1) + '/' + yearString.slice(yearString.length - 2);
			} else {
				return year;
			}
		case ClientTaskGroupType.Esign:
			return (taskGroup.tasks[0].params?.documents as string[])?.toString().split(',').join(', ');
		default:
			return '';
		}
	}

	async getStatusList() {
		const waitingOnClientLabel = await this.translate.get('onvio.clientTaskWidget.status.waitingOnClient').toPromise();
		const completedLabel = await this.translate.get('onvio.clientTaskWidget.status.completed').toPromise();
		const canceledLabel = await this.translate.get('onvio.clientTaskWidget.status.canceled').toPromise();
		const receivedLabel = await this.translate.get('onvio.clientTaskWidget.status.received').toPromise();
		const allLabel = await this.translate.get('Bluemoon.DMS.Grid.Approvals.Filter.All').toPromise();
		const statusList = [];
		statusList.push(
			{ key: 5, value: allLabel, name: 'all' },
			{ key: TaskStatus.WOC, value: waitingOnClientLabel, name: 'waitingonclient' },
			{ key: TaskStatus.Completed, value: completedLabel, name: 'completed' },
			{ key: TaskStatus.Canceled, value: canceledLabel, name: 'canceled' },
			{ key: TaskStatus.Received, value: receivedLabel, name: 'received' }
		);
		return statusList;
	}

	async getContactInfo(companyId: string, contactId: string): Promise<[string, boolean]> {
		const cachedContact = this.contactInfoCache.get(contactId);
		if (cachedContact) {
			return cachedContact;
		}
		try {
			const { userId, isClientCenterEnabled } =
				await this.http.get<any>(`/api/core/v7/companies/${companyId}/contacts/${contactId}`).toPromise();
			this.contactInfoCache.set(contactId, [userId, isClientCenterEnabled]);
			return [userId, isClientCenterEnabled];
		} catch {
			return null;
		}
	}

	async getCanceledEsignMessage(task: ClientTaskGroup) {
		const version = task.tasks[0].params.apiVersion;
		const url = `/api/signatures/v${version}/login`;
		const body = {
			eid: task.tasks[0].params.eid,
			sid: task.tasks[0].params.signatories[0].sid,
			uid: task.tasks[0].params.signatories[0].uid
		};
		try {
			const esign = await this.http.post<any>(url, body).toPromise();
			return esign.envelopeStatus.toLowerCase() === 'declined' ?
				await this.translate.get('onvio.clientTaskWidget.esign.rejected').toPromise() :
				await this.translate.get('onvio.clientTaskWidget.esign.canceled').toPromise();
		} catch {
			return this.translate.get('onvio.clientTaskWidget.esign.canceled').toPromise();
		}
	}

	getMaxWidth() {
		// width of view as button varries significantly based on language
		// want to prevent names truncating to early and buttons wrapping
		switch (this.currentLang) {
		case 'pt-BR':
			return '220px';
		default:
			return '250px';
		}
	}

}
